import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { getDownloadUrl, detectPlatform, PLATFORM_LABELS, DOWNLOAD_URLS } from '../lib/constants';
import { trackDownloadClick } from '../lib/analytics';
import AntivirusModal from './AntivirusModal';

const EASE = [0.32, 0.72, 0, 1] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 24, filter: 'blur(6px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.8, ease: EASE } },
};
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

export default function Hero() {
  const { t } = useTranslation();
  const scenes = t('hero.typewriterScenes', { returnObjects: true }) as string[];
  const [displayText, setDisplayText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPlatforms, setShowPlatforms] = useState(false);

  // 所有打字机可变状态放 ref，避免触发 re-render 导致抖动
  const stateRef = useRef({ sceneIndex: 0, charIndex: 0, isDeleting: false });
  const scenesRef = useRef<string[]>(scenes);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 保持 scenesRef 与最新翻译同步（语言切换场景）
  scenesRef.current = scenes;

  useEffect(() => {
    const tick = () => {
      const s = stateRef.current;
      const currentScene = scenesRef.current[s.sceneIndex] ?? '';

      if (!s.isDeleting) {
        if (s.charIndex < currentScene.length) {
          s.charIndex++;
          setDisplayText(currentScene.slice(0, s.charIndex));
          timeoutRef.current = setTimeout(tick, 60);
        } else {
          // 展示完整文字后暂停 2s 再删除
          timeoutRef.current = setTimeout(() => {
            s.isDeleting = true;
            timeoutRef.current = setTimeout(tick, 30);
          }, 2000);
        }
      } else {
        if (s.charIndex > 0) {
          s.charIndex--;
          setDisplayText(currentScene.slice(0, s.charIndex));
          timeoutRef.current = setTimeout(tick, 30);
        } else {
          // 删完后切换到下一句
          s.isDeleting = false;
          s.sceneIndex = (s.sceneIndex + 1) % scenesRef.current.length;
          timeoutRef.current = setTimeout(tick, 60);
        }
      }
    };

    timeoutRef.current = setTimeout(tick, 400);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []); // 只跑一次，打字机状态完全由 ref 管理

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackDownloadClick(detectPlatform());
    if (detectPlatform() === 'win_x64') {
      setShowModal(true);
    } else {
      window.location.href = getDownloadUrl();
    }
  };

  const handleConfirm = () => {
    setShowModal(false);
    window.location.href = getDownloadUrl();
  };

  return (
    <section className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-hidden pb-24 pt-36 md:pt-40">
      {/* 暖色径向光晕（GPU 安全：仅 transform 动画） */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="glow-clay absolute left-1/2 top-[16%] h-[38rem] w-[38rem] -translate-x-1/2 animate-float rounded-full opacity-70 blur-3xl" />
        <div className="glow-amber absolute right-[6%] top-[44%] h-[26rem] w-[26rem] animate-float-slow rounded-full opacity-50 blur-3xl" />
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative mx-auto max-w-4xl px-6 text-center"
      >
        {/* eyebrow */}
        <motion.div variants={fadeUp} className="mb-7 flex justify-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] bg-[#faf9f5]/60 px-4 py-1.5 text-[#6b6a68] backdrop-blur-sm dark:border-[#faf9f51a] dark:bg-[#2b2a27]/50 dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('hero.eyebrow', '桌面 AI 助手 · 本地运行')}
          </span>
        </motion.div>

        {/* 标题 — Fraunces 衬线展示字体 */}
        <motion.h1
          variants={fadeUp}
          className="font-display display-tight text-5xl font-medium leading-[1.05] text-[#141413] dark:text-[#faf9f5] md:text-7xl lg:text-[5rem]"
        >
          {t('hero.title')}
          <br />
          <span className="text-[#ae5630]">{t('hero.titleHighlight')}</span>
        </motion.h1>

        {/* 描述 */}
        <motion.p
          variants={fadeUp}
          className="mx-auto mt-7 max-w-xl text-lg leading-relaxed text-[#6b6a68] dark:text-[#9a9893] md:text-xl"
        >
          {t('hero.description')}
        </motion.p>

        {/* 终端卡 — 双层 bezel 结构 */}
        <motion.div variants={fadeUp} className="mx-auto mt-12 max-w-xl">
          <div className="rounded-[1.5rem] border border-[#1414131a] bg-[#1414130a] p-1.5 shadow-warm dark:border-[#faf9f51a] dark:bg-[#faf9f50a]">
            <div className="overflow-hidden rounded-[calc(1.5rem-0.375rem)] border border-[#1414130d] bg-[#fffefb] shadow-inner-glow dark:border-[#faf9f50d] dark:bg-[#1a1918]">
              {/* 窗口栏 */}
              <div className="flex items-center gap-2 border-b border-[#1414130d] px-4 py-3 dark:border-[#faf9f50d]">
                <span className="h-2.5 w-2.5 rounded-full bg-[#d98c73]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#d8be8a]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#9fb185]" />
                <span className="ml-2 font-mono text-xs text-[#87867f]">cherry-agent</span>
              </div>
              {/* 终端正文 */}
              <div className="px-5 py-5 text-left">
                <div
                  className="font-mono text-sm leading-relaxed text-[#6b6a68] dark:text-[#9a9893] md:text-[15px]"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <span className="text-[#ae5630]">❯ </span>
                  <span>{displayText}</span>
                  <span className="animate-blink text-[#ae5630]">▍</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div variants={fadeUp} className="mt-11 flex flex-col items-center gap-4">
          <a
            href={getDownloadUrl()}
            onClick={handleDownloadClick}
            className="group inline-flex items-center gap-3 rounded-full bg-[#ae5630] py-2 pl-7 pr-2 text-base font-medium text-white shadow-warm transition-all duration-300 ease-spring hover:bg-[#c4633a] active:scale-[0.98]"
          >
            {t('hero.downloadBtn')}（{PLATFORM_LABELS[detectPlatform()]}）
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition-transform duration-300 ease-spring group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
              <ArrowUpRight size={18} />
            </span>
          </a>
          <button
            onClick={() => setShowPlatforms((prev) => !prev)}
            className="inline-flex items-center gap-1 text-sm text-[#6b6a68] transition-colors duration-300 hover:text-[#ae5630] dark:text-[#9a9893] dark:hover:text-[#ae5630]"
          >
            {t('hero.otherPlatforms', '其他版本')}
            <ChevronDown size={14} className={`transition-transform duration-300 ${showPlatforms ? 'rotate-180' : ''}`} />
          </button>
          {showPlatforms && (
            <div className="flex flex-wrap items-center justify-center gap-3">
              {Object.entries(DOWNLOAD_URLS).map(([key, url]) => (
                <a
                  key={key}
                  href={url}
                  onClick={(e) => {
                    e.preventDefault();
                    trackDownloadClick(key);
                    if (key === 'win_x64') {
                      setShowModal(true);
                    } else {
                      window.location.href = url;
                    }
                  }}
                  className="rounded-full border border-[#1414131a] px-4 py-2 text-sm text-[#6b6a68] transition-all duration-300 hover:border-[#ae5630] hover:text-[#ae5630] dark:border-[#faf9f51a] dark:text-[#9a9893] dark:hover:text-[#ae5630]"
                >
                  {PLATFORM_LABELS[key]}
                </a>
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>

      <AntivirusModal
        open={showModal}
        onConfirm={handleConfirm}
        onClose={() => setShowModal(false)}
      />
    </section>
  );
}
